function initializeFaqAccordion(root) {
  // Delegated on root rather than attaching a listener per item found at
  // init time: querySelectorAll returns a static snapshot, and the editor
  // canvas can re-render items inside an already-initialized root (ACF
  // updates the preview in place on field changes without necessarily
  // replacing the root node) — a one-time snapshot would leave newly added
  // items with no click handler and keep referencing removed ones.
  root.addEventListener('click', (event) => {
    const question = event.target.closest('.faq__question');

    if (!question) {
      return;
    }

    const item = question.closest('.faq__item');
    const answer = item ? item.querySelector('.faq__answer') : null;

    if (!item || !answer) {
      return;
    }

    const isOpen = item.classList.contains('faq__item--open');

    // Close all other FAQ items. Queried fresh on every click, scoped to
    // this block's own document (the frontend page, or the editor canvas
    // iframe) via root.ownerDocument, matching the original single-document
    // behavior while staying correct inside the iframe realm.
    root.ownerDocument.querySelectorAll('.faq__item').forEach((otherItem) => {
      if (otherItem !== item) {
        otherItem.classList.remove('faq__item--open');
        const otherQuestion = otherItem.querySelector('.faq__question');
        const otherAnswer = otherItem.querySelector('.faq__answer');
        if (otherQuestion) {
          otherQuestion.setAttribute('aria-expanded', 'false');
        }
        if (otherAnswer) {
          otherAnswer.style.maxHeight = null;
        }
      }
    });

    // Toggle current item
    if (isOpen) {
      item.classList.remove('faq__item--open');
      question.setAttribute('aria-expanded', 'false');
      answer.style.maxHeight = null;
    } else {
      item.classList.add('faq__item--open');
      question.setAttribute('aria-expanded', 'true');
      answer.style.maxHeight = `${answer.scrollHeight}px`;
    }
  });
}

function initializeFaqPopups(doc) {
  if (doc.__faqPopupsInitialized) {
    return;
  }

  doc.__faqPopupsInitialized = true;

  // Delegated on the document rather than per-element listeners found at
  // init time, for the same reason as the accordion above: triggers/popups
  // can be added or replaced later by the editor's re-renders. Trigger and
  // closer matches are checked before any generic "clicked inside content"
  // fallback so a closer nested inside .faq-popup__content (see faq.twig:
  // .faq-popup__close lives inside .faq-popup__content) still closes the
  // popup — a click elsewhere inside the content that matches neither is
  // simply a no-op, which is what the original per-element
  // stopPropagation() on .faq-popup__content amounted to in practice.
  doc.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-popup-trigger]');

    if (trigger) {
      e.preventDefault();

      const popupId = trigger.getAttribute('data-popup-trigger');
      const popup = doc.getElementById(popupId);

      if (popup) {
        popup.classList.add('faq-popup--visible');
        popup.setAttribute('aria-hidden', 'false');
        doc.body.classList.add('popup-open');

        // Focus trap
        const focusableElements = popup.querySelectorAll(
          'button, [href], input, select, textarea, '
          + '[tabindex]:not([tabindex="-1"])',
        );

        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }

      return;
    }

    const closer = e.target.closest('.faq-popup__close, .faq-popup__overlay');

    if (closer) {
      const popup = closer.closest('.faq-popup');
      if (popup) {
        popup.classList.remove('faq-popup--visible');
        popup.setAttribute('aria-hidden', 'true');
        doc.body.classList.remove('popup-open');
      }
    }
  });

  // Close popup on ESC key
  doc.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const visiblePopup = doc.querySelector('.faq-popup--visible');
      if (visiblePopup) {
        visiblePopup.classList.remove('faq-popup--visible');
        visiblePopup.setAttribute('aria-hidden', 'true');
        doc.body.classList.remove('popup-open');
      }
    }
  });
}

function initializeFaqBlock(root) {
  if (!root || root.dataset.faqInitialized === 'true') {
    return;
  }

  const faqItems = root.querySelectorAll('.faq__item');

  if (!faqItems.length) {
    return;
  }

  root.dataset.faqInitialized = 'true';

  initializeFaqAccordion(root);
  initializeFaqPopups(root.ownerDocument);
}

function initializeFrontend() {
  document.querySelectorAll('.faq').forEach(initializeFaqBlock);
}

if (window.acf) {
  window.acf.addAction('render_block_preview/type=faq', ($block) => {
    initializeFaqBlock($block.get(0));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFrontend);
} else {
  initializeFrontend();
}
